/** @format */
import { ChangeDetectionStrategy, Component, inject, signal, ViewEncapsulation } from '@angular/core';
import { OtmId } from 'common-typescript/types';
import { catchError, EMPTY, Observable, throwError } from 'rxjs';
import { LocaleService } from 'sis-common';
import { AppErrorHandler, StudyVoucherPaymentService } from 'sis-components';
import { OpenUniversityErrorHelperService } from '../../../service/open-university-error-helper.service';

@Component({
    selector: 'app-study-voucher-payment-failed',
    standalone: false,
    templateUrl: './study-voucher-payment-failed.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudyVoucherPaymentFailedComponent {
    private readonly appErrorHandler = inject(AppErrorHandler);
    private readonly localeService = inject(LocaleService);
    private readonly openUniversityErrorHelper = inject(OpenUniversityErrorHelperService);
    private readonly studyVoucherPaymentService = inject(StudyVoucherPaymentService);

    protected readonly requestInProgress = signal<boolean>(false);

    protected retryPayment(cartId: OtmId): void {
        if (this.requestInProgress()) {
            return;
        }
        this.requestInProgress.set(true);
        this.studyVoucherPaymentService
            .retryCreatingStudyVoucherPaymentAndNavigateToOpinFi(cartId, this.localeService.getCurrentLanguage())
            .pipe(
                catchError(error => this.handlePaymentInitializationError(error)),
                this.appErrorHandler.defaultErrorHandler(),
            )
            .subscribe()
            .add(() => {
                this.requestInProgress.set(false);
            });
    }

    /**
     * Lets the localized descriptions of rejected payments (HTTP 422) through to the default error handler, and shows the generic payment
     * initialization failure dialog for everything else. Matches how `OpenUniversityCartComponent` handles the same backend errors when the
     * payment is created for the first time.
     */
    private handlePaymentInitializationError(error: any): Observable<never> {
        if (error?.status === 422) {
            return throwError(() => error);
        }
        this.openUniversityErrorHelper.showPaymentInitializationFailureAlert();
        return EMPTY;
    }
}
